import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback, useMemo,
} from 'react';
import { Entity } from '../../types/entities/Entity';
import {
  IconButton,
} from '@mui/material';
import api from '../../services/api';
import {FilterAltOutlined, FilterList} from "@mui/icons-material";
import {DataGridPro, GridColDef} from "@mui/x-data-grid-pro";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import {useGridApiRef} from "@mui/x-data-grid";
import ReactDOM from "react-dom";

type Props<T extends Entity> = {
  values: any;
  allValues: any;
  onChange: (value: any, column: string) => void;
};

const renderData = (data:any, depth = 0, mr:boolean = false) => {
  const x = mr ? 0 : 20;

  const indentStyle = {
    marginLeft: `${depth * 20}px`, // A mélység alapján növeljük a margót
  };

  if (typeof data === "object" && data !== null) {
    if (Array.isArray(data)) {
      // Ha tömb, listázd az elemeket
      return (
        <ul style={indentStyle}>
          {data.map((item, index) => (
            <li key={index}>{renderData(item, depth + 1)}</li>
          ))}
        </ul>
      );
    } else {
      // Ha objektum, jelenítsd meg a kulcs-érték párokat
      return (
        <ul style={indentStyle}>
          {Object.entries(data).map(([key, value]) => (
            <li key={key}>
              <span><b>{key}:</b> <span>{renderData(value, depth + 1)}</span></span>
            </li>
          ))}
        </ul>
      );
    }
  }
  // Ha primitív érték, egyszerűen jelenítsd meg
  return <span>{data !== null ? data.toString() : "null"}</span>;
};


export const ServiceReservationLog = <T extends Entity>({
  values,
  allValues,
  onChange,
}: Props<T>): ReactElement => {
  const [properties, setProperties] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openHeaderFilters, setOpenHeaderFilters] = useState(false);
  const apiRef = useGridApiRef();
  const autosizeOptions = {
    includeHeaders: true,
    includeOutliers: true,
    outliersFactor: 1.5,
    expand: false,
  };

  const fetchData = useCallback(() => {
    const searchParams = new URLSearchParams({});
    setLoading(true);
    api
      .get(`/service-reservations/log/${values.id}`, searchParams)
      .then((res: any) => {
        ReactDOM.flushSync(() => {
          if (apiRef?.current) {
            apiRef.current.updateRows(res);
          }
        });
        setProperties(res);
        setLoading(false);
      })
      .then(() => sleep(0))
      .then(() => {
        if (apiRef?.current) {
          apiRef.current.autosizeColumns(autosizeOptions);
        }
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const columns: GridColDef[] = useMemo(() => {
    const columnArray: GridColDef[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        cellClassName: 'actions',
        maxWidth: 50,
        getActions: (props: any) => {
          return [<>{props?.row?.row_number}</>];
        },
        renderHeader: (props: any) => {
          return (
            <IconButton
              size={'small'}
              onClick={() => {
                setOpenHeaderFilters(!openHeaderFilters);
              }}
            >
              <FilterAltOutlined
                sx={{
                  color: '#fff',
                }}
              />
            </IconButton>
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Created',
        editable: false,
        type: 'dateTime',
        valueGetter: (params) => params.value ? new Date(params.value) : null, // ⬅️ Biztosítja, hogy `Date` objektum legyen
      },
      {
        field: 'user',
        headerName: 'User',
        editable: false,
        type: 'string',
        renderCell: (params: any) => {
          return <div>{params?.value?.name}</div>;
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        editable: false,
        type: 'string',
      },
      {
        field: 'name',
        headerName: 'Name',
        editable: false,
        type: 'string',
        renderCell: (params:any) =>{
          const value:any = params?.value;
          return renderData(value);
        },
      },
      {
        field: 'changes',
        headerName: 'Changes',
        editable: false,
        type: 'string',
        renderCell: (params:any) =>{
          const value:any = params?.value;
          return renderData(value);
        },
      },
    ];
    return columnArray;
  }, []);

  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <DataGridPro
        apiRef={apiRef}
        sx={{
          pointerEvents: loading ? 'none' : 'auto',
          opacity: loading ? 0.5 : 1,
          '& .MuiDataGrid-columnHeaders': {
            background: '#256861',
            color: '#fefefe',
          },
          '& .MuiDataGrid-pinnedColumnHeaders': {
            background: '#1a5c55',
            boxShadow: 'none',
          },
        }}
        unstable_headerFilters={openHeaderFilters}
        rows={properties}
        getRowClassName={(params: any) => {
          const color =
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
          return `${color}`;
        }}
        disableColumnMenu={true}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        columnHeaderHeight={38}
        getRowHeight={(row)=> 'auto'}
        hasPagination={true}
        pagination={true}
        paginationMode={'server'}
        filterMode={'server'}
        sortingMode={'server'}
        slots={{
          // @ts-ignore
          columnSortedDescendingIcon: CustomSortIconDesc,
          columnSortedAscendingIcon: CustomSortIconAsc,
          columnUnsortedIcon: CustomSortIcon,
          filterIcon: FilterList,
          headerFilterMenu: null,
        }}
        pageSizeOptions={[5000]}
      />
    </>
  );
};

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}